import { Box, Container, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, NewPageTitleBar } from '@/components';
import { getMenuData } from '@core/data/menuData';
import { UserApiResponse } from '@/services/userService';
import NewWcnTable, { DataTableColumn, DataTableSorting } from '@/components/datatable/NewWcnTable.tsx';
import { useRegisterRefresh } from '@/components/shared/navigation/securePage';
import { DateTime } from 'luxon';

interface UserListPageProps {
    title: string;
    routePath: string;
    users: UserApiResponse[];
    loading: boolean;
    sorting: DataTableSorting;
    columns: DataTableColumn<UserApiResponse>[];
    onRefresh: () => Promise<void>;
    onAdd: () => void;
    children: React.ReactNode;
    lastUpdate?: DateTime;
}

export const UserListPage = ({
    title,
    routePath,
    users,
    loading,
    sorting,
    columns,
    onRefresh,
    onAdd,
    children,
    lastUpdate
}: UserListPageProps) => {
    const { t } = useTranslation();

    useRegisterRefresh(onRefresh, loading);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-account" style={{ fontSize: '16px' }} />}
                title={title}
                menus={getMenuData()}
                currentRoute={{ path: routePath }}
                iconSize="21px"
                t={t}
                lastUpdate={lastUpdate}
            />

            <Box sx={{ width: '100%', px: 4, pb: 1, '@media (max-width: 480px)': { pt: 3 } }}>
                <Button
                    variant="text"
                    onClick={onAdd}
                    disabled={loading}
                    className="pagetitlebar-action-btn pagetitlebar-action-btn--default"
                >
                    <Box className="pagetitlebar-action-btn-icon">
                        <i
                            className="bi bi-plus-lg"
                            style={{ fontSize: '16px', color: 'inherit', lineHeight: 1, paddingRight: 6 }}
                        />
                    </Box>
                    <span>{t('add')}</span>
                </Button>
            </Box>

            {loading && <LoadingSpinner showBackground={false} />}

            <Container maxWidth={false} sx={{ flex: 1, py: 2 }}>
                {!loading && users.length > 0 && (
                    <NewWcnTable
                        columns={columns}
                        data={users}
                        noDataMessage={t('noData')}
                        sorting={sorting}
                        size="small"
                    />
                )}

                {!loading && users.length === 0 && <Typography align="center">{t('noData')}</Typography>}
            </Container>

            {children}
        </Box>
    );
};
